import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [token, setToken] = useState(null);
  const [user, setUser] = useState(null);

  // Configure axios defaults
  axios.defaults.baseURL = process.env.REACT_APP_API_URL;
  
  // Add token to all requests
  axios.interceptors.request.use((config) => {
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  const validateToken = useCallback(async (tokenToValidate) => {
    try {
      const response = await axios.post(
        process.env.REACT_APP_OAUTH_VERIFY_ENDPOINT,
        {},
        {
          headers: { Authorization: `Bearer ${tokenToValidate}` }
        }
      );
      setUser(response.data.user);
      setIsAuthenticated(true);
    } catch (error) {
      console.error('Token validation failed:', error);
      logout();
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Check token on mount
  useEffect(() => {
    const storedToken = localStorage.getItem(process.env.REACT_APP_TOKEN_STORAGE_KEY);
    if (storedToken) {
      setToken(storedToken);
      validateToken(storedToken);
    } else {
      setIsLoading(false);
    }
  }, [validateToken]);

  const handleCallback = async (code) => {
    try {
      console.log('Handling OAuth callback with code:', code);
      
      // Construct the token endpoint URL
      const tokenEndpoint = `${process.env.REACT_APP_OAUTH_SERVER_URL}/o/token/`;
      console.log('Token endpoint:', tokenEndpoint);
      
      // Ensure redirect URI matches exactly what was used in authorization
      const redirectUri = process.env.REACT_APP_OAUTH_REDIRECT_URI;
      console.log('Using redirect URI:', redirectUri);
      
      // Prepare the request data
      const requestData = new URLSearchParams({
        grant_type: 'authorization_code',
        code: code,
        client_id: process.env.REACT_APP_OAUTH_CLIENT_ID,
        client_secret: process.env.REACT_APP_OAUTH_CLIENT_SECRET,
        redirect_uri: redirectUri,
        scope: process.env.REACT_APP_OAUTH_SCOPE || 'read'
      });

      console.log('Token request data:', Object.fromEntries(requestData));
      
      // Exchange authorization code for tokens
      const response = await axios.post(
        tokenEndpoint,
        requestData.toString(),
        {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            'Accept': 'application/json'
          }
        }
      );

      console.log('Token response:', response.data);
      
      const { access_token, refresh_token, expires_in, user: userData } = response.data;
      
      // Store tokens and expiry
      setToken(access_token);
      localStorage.setItem(process.env.REACT_APP_TOKEN_STORAGE_KEY, access_token);
      localStorage.setItem(process.env.REACT_APP_REFRESH_TOKEN_STORAGE_KEY, refresh_token);
      localStorage.setItem(
        process.env.REACT_APP_TOKEN_EXPIRY_KEY,
        Date.now() + (expires_in * 1000)
      );
      
      setUser(userData);
      setIsAuthenticated(true);
      return true;
    } catch (error) {
      console.error('OAuth callback failed:', error.response?.data || error);
      if (error.response?.data) {
        console.error('Error details:', {
          error: error.response.data.error,
          error_description: error.response.data.error_description,
          status: error.response.status
        });
      }
      throw error;
    }
  };

  const login = async (username, password) => {
    try {
      const response = await axios.post(process.env.REACT_APP_OAUTH_TOKEN_ENDPOINT, {
        username,
        password,
        client_id: process.env.REACT_APP_OAUTH_CLIENT_ID,
        client_secret: process.env.REACT_APP_OAUTH_CLIENT_SECRET,
        grant_type: 'password',
        scope: process.env.REACT_APP_OAUTH_SCOPE
      });
      
      const { access_token, refresh_token, expires_in, user: userData } = response.data;
      
      // Store tokens and expiry
      setToken(access_token);
      localStorage.setItem(process.env.REACT_APP_TOKEN_STORAGE_KEY, access_token);
      localStorage.setItem(process.env.REACT_APP_REFRESH_TOKEN_STORAGE_KEY, refresh_token);
      localStorage.setItem(
        process.env.REACT_APP_TOKEN_EXPIRY_KEY,
        Date.now() + (expires_in * 1000)
      );
      
      setUser(userData);
      setIsAuthenticated(true);
      return true;
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    }
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    setIsAuthenticated(false);
    localStorage.removeItem(process.env.REACT_APP_TOKEN_STORAGE_KEY);
    localStorage.removeItem(process.env.REACT_APP_REFRESH_TOKEN_STORAGE_KEY);
    localStorage.removeItem(process.env.REACT_APP_TOKEN_EXPIRY_KEY);
  };

  const refreshToken = useCallback(async () => {
    try {
      const refresh_token = localStorage.getItem(process.env.REACT_APP_REFRESH_TOKEN_STORAGE_KEY);
      if (!refresh_token) {
        throw new Error('No refresh token available');
      }

      const response = await axios.post(
        process.env.REACT_APP_OAUTH_REFRESH_ENDPOINT,
        {
          client_id: process.env.REACT_APP_OAUTH_CLIENT_ID,
          client_secret: process.env.REACT_APP_OAUTH_CLIENT_SECRET,
          grant_type: 'refresh_token',
          refresh_token
        }
      );

      const { access_token, refresh_token: new_refresh_token, expires_in } = response.data;
      
      // Update tokens and expiry
      setToken(access_token);
      localStorage.setItem(process.env.REACT_APP_TOKEN_STORAGE_KEY, access_token);
      localStorage.setItem(process.env.REACT_APP_REFRESH_TOKEN_STORAGE_KEY, new_refresh_token);
      localStorage.setItem(
        process.env.REACT_APP_TOKEN_EXPIRY_KEY,
        Date.now() + (expires_in * 1000)
      );
      
      return true;
    } catch (error) {
      console.error('Token refresh failed:', error);
      logout();
      return false;
    }
  }, []);

  return (
    <AuthContext.Provider value={{
      isAuthenticated,
      isLoading,
      token,
      user,
      login,
      logout,
      handleCallback,
      refreshToken
    }}>
      {children}
    </AuthContext.Provider>
  );
}; 